import { useI18n } from "vue-i18n";
import { platformMessages } from "./platform";

/**
 * THE ONE DOOR into the platform console's dictionary.
 *
 * `locale` comes back because half this console is instants — when an audit line
 * was written, when a support session ends, when an error was last seen — and
 * every one of them used to be a hardcoded `toLocaleString("pt-BR")`. Those are
 * the assumption ADR-018 refuses, and the kind that survives every test because
 * a date always renders something.
 *
 * `ago` rides along for the same reason the two screens that need it would
 * otherwise each grow their own: "há 5 min" written twice is two translations of
 * one sentence, and they drift.
 */
export function usePlatformI18n() {
	const { t, te, locale } = useI18n({
		useScope: "local",
		inheritLocale: true,
		messages: platformMessages,
	});

	/** How long ago, in the words the errors list uses. */
	function ago(iso: string): string {
		const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
		if (minutes < 1) return t("errors.now");
		if (minutes < 60) return t("errors.minutesAgo", { n: minutes });
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return t("errors.hoursAgo", { n: hours });
		return t("errors.daysAgo", { n: Math.floor(hours / 24) });
	}

	/** A stored instant, in the language the panel is being read in. */
	function at(iso: string): string {
		return new Date(iso).toLocaleString(locale.value, {
			dateStyle: "short",
			timeStyle: "short",
		});
	}

	return { t, te, locale, ago, at };
}
